import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * Wire schemas for `/admin/support-tickets/*` — Wave 2.63 BE-1 (TASK-100).
 *
 * Backed by `customers.support_tickets` + `customers.ticket_messages`
 * (migration 0005). Surfaces the admin support desk side: list / view /
 * reply / patch (assign / status / priority). Client-side ticket creation
 * is out of MVP scope (PRD §9.6) — only the admin response surface ships.
 *
 * State machine mirrors the DB CHECK constraint:
 *   `open` → `in_progress` → `waiting_client` → `closed`.
 *
 * Allowed transitions (admin-arch §2.3, mirrored in the service):
 *   - open            → in_progress | waiting_client | closed
 *   - in_progress     → waiting_client | closed
 *   - waiting_client  → in_progress | closed
 *   - closed          → in_progress  (re-open)
 *
 * Priority enum mirrors the DB CHECK on `priority`.
 */
export const supportTicketStatusSchema = z.enum([
    'open',
    'in_progress',
    'waiting_client',
    'closed',
]);
export const supportTicketPrioritySchema = z.enum(['P0', 'P1', 'P2', 'P3']);
const BODY_MAX = 20_000;
const SEARCH_MAX = 200;
const CURSOR_MAX = 200;
/**
 * List item — flat row consumed by the admin DataTable. Pulls a couple of
 * denormalised fields (`account_name`, `assigned_admin_name`) via JOIN in
 * the service so the FE does not need a follow-up call per row.
 */
export const supportTicketListItemSchema = z.object({
    id: uuidSchema,
    account_id: uuidSchema,
    account_name: z.string().nullable(),
    author_user_id: uuidSchema.nullable(),
    subject: z.string(),
    status: supportTicketStatusSchema,
    priority: supportTicketPrioritySchema,
    assigned_admin_id: uuidSchema.nullable(),
    assigned_admin_name: z.string().nullable(),
    created_at: isoDateTimeSchema,
    updated_at: isoDateTimeSchema,
    first_response_at: isoDateTimeSchema.nullable(),
});
export const supportTicketListResponseSchema = z.object({
    items: z.array(supportTicketListItemSchema),
    next_cursor: z.string().nullable(),
});
/**
 * `GET /admin/support-tickets` query params.
 *
 * Sort is fixed `updated_at DESC` server-side (per brief). `q` matches the
 * subject (ILIKE). Keyset cursor encodes `(updated_at, id) DESC` — see
 * service for the encoding.
 */
export const supportTicketFilterSchema = z
    .object({
    status: supportTicketStatusSchema.optional(),
    assignee_id: uuidSchema.optional(),
    account_id: uuidSchema.optional(),
    priority: supportTicketPrioritySchema.optional(),
    q: z.string().min(1).max(SEARCH_MAX).optional(),
    cursor: z.string().min(1).max(CURSOR_MAX).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
})
    .strict();
/**
 * Thread message inside a ticket (`customers.ticket_messages`).
 */
export const supportTicketMessageSchema = z.object({
    id: uuidSchema,
    ticket_id: uuidSchema,
    author_user_id: uuidSchema.nullable(),
    author_display_name: z.string().nullable(),
    body: z.string(),
    is_internal_note: z.boolean(),
    created_at: isoDateTimeSchema,
});
/**
 * Single attachment — Wave 2.64 BE (TASK-285). Rendered as an inline
 * download chip in the FE thread header. `download_url` is a short-TTL
 * signed S3 URL minted by the backend per-request — never a raw S3 key.
 */
export const supportTicketAttachmentSchema = z.object({
    id: uuidSchema,
    ticket_id: uuidSchema,
    /**
     * Nullable because the underlying `customers.ticket_attachments
     * .uploaded_by` column was created with `ON DELETE SET NULL` (migration
     * 0005). Wave 2.64 always stamps the actor at upload time, but a hard-
     * deleted user would surface NULL here.
     */
    uploaded_by_user_id: uuidSchema.nullable(),
    file_name: z.string(),
    content_type: z.string(),
    byte_size: z.number().int().nonnegative(),
    created_at: isoDateTimeSchema,
});
/**
 * Full ticket view — header + thread. Returned by GET /:id.
 */
export const supportTicketDetailSchema = z.object({
    id: uuidSchema,
    account_id: uuidSchema,
    account_name: z.string().nullable(),
    author_user_id: uuidSchema.nullable(),
    author_display_name: z.string().nullable(),
    subject: z.string(),
    description: z.string().nullable(),
    status: supportTicketStatusSchema,
    priority: supportTicketPrioritySchema,
    assigned_admin_id: uuidSchema.nullable(),
    assigned_admin_name: z.string().nullable(),
    created_at: isoDateTimeSchema,
    updated_at: isoDateTimeSchema,
    first_response_at: isoDateTimeSchema.nullable(),
    messages: z.array(supportTicketMessageSchema),
    /**
     * Attachments uploaded against the ticket. Empty array when none.
     * Wave 2.64 BE (TASK-285).
     */
    attachments: z.array(supportTicketAttachmentSchema),
});
/**
 * `POST /admin/support-tickets/:id/attachments` response.
 * Wave 2.64 BE (TASK-285).
 */
export const supportTicketAttachmentCreateResponseSchema = z.object({
    attachment: supportTicketAttachmentSchema,
});
/**
 * `POST /admin/support-tickets/:id/reply` body.
 *
 * `body` — the admin's reply text (markdown allowed, rendered FE-side).
 * `is_internal_note` — when true the message is hidden from the customer
 * (PRD §9.6). Defaults to false.
 *
 * Reply also recomputes `support_tickets.updated_at = now()` and, when the
 * ticket was in `open` (no prior admin response), stamps `first_response_at`
 * + transitions status to `waiting_client`.
 */
export const supportTicketReplyRequestSchema = z
    .object({
    body: z.string().trim().min(1).max(BODY_MAX),
    is_internal_note: z.boolean().optional().default(false),
})
    .strict();
/**
 * `PATCH /admin/support-tickets/:id` body — at least one field required.
 *
 * Status transitions validated server-side against the state machine.
 * `assignee_user_id` may be set to `null` to unassign.
 */
export const supportTicketPatchSchema = z
    .object({
    status: supportTicketStatusSchema.optional(),
    assignee_user_id: uuidSchema.nullable().optional(),
    priority: supportTicketPrioritySchema.optional(),
})
    .strict()
    .refine((obj) => Object.keys(obj).length > 0, 'At least one field must be provided');
//# sourceMappingURL=support-tickets.js.map